import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineQrcode } from "react-icons/ai";
import QRCode from "react-qr-code";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { getQRToken, marcarQrColocado } from "../../services";
// El tamaño del papel y como se reparte la etiqueta estan en etiquetaQr.js
import { CABEZAL_PX, centrarEnCabezal, componerEtiqueta, lineaCodigoPostal } from "./etiquetaQr";

// Sentido de impresion de la B1, que se sustituye por el que declare la impresora
const SENTIDO_IMPRESION = "top";

// Prefijos de los modelos que se usan en campo. Sin esto la libreria busca por la
// inicial de sus 77 modelos y el selector de Chrome lista medio barrio
const PREFIJOS_IMPRESORA = ["B1", "B2", "B3", "B4", "D1"];
// La libreria solo espera 1 s a la primera respuesta de la impresora, y la B1 recien
// conectada tarda mas: el primer intento fallaba y el segundo ya funcionaba
const INTENTOS_CONEXION = 3;
const PAUSA_REINTENTO_MS = 1000;
const hayBluetooth = () => typeof navigator !== "undefined" && !!navigator.bluetooth;

// Colores del icono segun el estado del QR en la finca
const COLOR_SIN_COLOCAR = "inherit";
const COLOR_SOLO_CAE = "#3eae3e";
const COLOR_CON_OMNIA = "#0097a7";

/**
 * Boton que genera el QR de una comunidad y lo muestra en un modal para imprimirlo.
 * Permite ademas marcar que el QR ya esta pegado en la finca: cuando lo esta, el icono
 * pasa a verde, o a verde azulado si el QR tambien da acceso a Omnia Hogar.
 * Se usa en el listado de Servicios y en el de Comunidades del tecnico.
 */
const BotonGenerarQR = ({ nif, workcenterId = null, qrColocado = false, omniaHogar = false }) => {
  const { t } = useTranslation('qr');
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [token, setToken] = useState("");
  const [datosComunidad, setDatosComunidad] = useState(null);
  const [preparandoEtiqueta, setPreparandoEtiqueta] = useState(false);
  const areaQr = useRef(null);
  const [error, setError] = useState("");
  const [colocado, setColocado] = useState(qrColocado);
  const [guardandoColocado, setGuardandoColocado] = useState(false);

  const abrir = async () => {
    setToken("");
    setError("");
    setAbierto(true);
    setCargando(true);
    try {
      const data = await getQRToken(nif, workcenterId);
      if (!data || !data.token) throw new Error("Error generando token");
      setToken(data.token);
      setDatosComunidad(data);
    } catch (e) {
      setError(t('button.generateError'));
    } finally {
      setCargando(false);
    }
  };

  const cerrar = () => {
    setAbierto(false);
    setToken("");
    setDatosComunidad(null);
    setError("");
  };

  const cambiarColocado = async () => {
    const nuevoValor = !colocado;
    setGuardandoColocado(true);
    try {
      const res = await marcarQrColocado(nif, workcenterId, nuevoValor);
      if (!res || res.qrColocado !== nuevoValor) throw new Error("No se pudo guardar");
      setColocado(nuevoValor);
      toast.success(nuevoValor ? t('button.placedOk') : t('button.removedOk'));
    } catch (e) {
      toast.error(t('button.saveError'));
    } finally {
      setGuardandoColocado(false);
    }
  };

  // Leyenda del modal: se resalta el estado en el que esta esta comunidad
  const leyenda = [
    { clave: "button.legendNotPlaced", color: "#000", activo: !colocado },
    { clave: "button.legendPlacedCae", color: COLOR_SOLO_CAE, activo: colocado && !omniaHogar },
    { clave: "button.legendPlacedOmnia", color: COLOR_CON_OMNIA, activo: colocado && omniaHogar },
  ];

  // Dibuja la etiqueta con el QR del modal y la direccion de la comunidad
  const prepararEtiqueta = async (cabezalPx = CABEZAL_PX) => {
    const { lienzo } = await componerEtiqueta({
      svg: areaQr.current?.querySelector("svg"),
      partes: [
        datosComunidad?.direccion,
        lineaCodigoPostal(datosComunidad?.codigoPostal, datosComunidad?.municipio),
      ],
      cabezalPx,
    });
    return lienzo;
  };

  const etiquetaComoPng = (lienzo) =>
    new Promise((listo) => lienzo.toBlob(listo, "image/png"));

  // Envia la etiqueta directamente a la impresora por Bluetooth, sin pasar por la app
  const imprimirPorBluetooth = async () => {
    const { NiimbotBluetoothClient, ImageEncoder } = await import("@mmote/niimbluelib");
    const cliente = new NiimbotBluetoothClient();
    try {
      // Se filtra por el servicio propio de NIIMBOT y, como respaldo para las que no
      // lo anuncian, por el nombre del modelo
      const servicios = cliente.getServiceUuidFilter();
      const dispositivo = await navigator.bluetooth.requestDevice({
        filters: [
          { services: servicios },
          ...PREFIJOS_IMPRESORA.map((prefijo) => ({ namePrefix: prefijo })),
        ],
        optionalServices: servicios,
      });
      // Se reintenta con el mismo dispositivo, sin volver a mostrar el selector
      for (let intento = 1; ; intento++) {
        try {
          await cliente.connect({ authorizedDevice: dispositivo });
          break;
        } catch (e) {
          if (intento >= INTENTOS_CONEXION) throw e;
          console.log(`Conexion con la impresora fallida (intento ${intento}), se reintenta`, e);
          await new Promise((listo) => setTimeout(listo, PAUSA_REINTENTO_MS));
        }
      }
      // Cada modelo declara su cabezal, su sentido de impresion y su densidad
      const modelo = cliente.getModelMetadata();
      const cabezal = modelo?.printheadPixels ?? CABEZAL_PX;
      // A la impresora va el ancho completo del cabezal; la imagen guardada, solo la etiqueta
      const lienzo = centrarEnCabezal(await prepararEtiqueta(cabezal), cabezal);
      const imagen = ImageEncoder.encodeCanvas(lienzo, 0, modelo?.printDirection ?? SENTIDO_IMPRESION);
      // Si el modelo no se reconoce se usa el algoritmo de la B1, que es la impresora del equipo
      const tarea = cliente.protocol.newPrintTask(cliente.getPrintTaskType() ?? "B1", {
        totalPages: 1,
        density: modelo?.densityDefault ?? 3,
      });
      try {
        await tarea.printInit();
        await tarea.printPage(imagen, 1);
        await tarea.waitForPageFinished();
        await tarea.waitForFinished();
      } finally {
        await cliente.protocol.printEnd();
      }
      toast.success(t('button.btPrinted'));
    } finally {
      try { await cliente.disconnect(); } catch (e) { /* ya estaba desconectada */ }
    }
  };

  // El movil ofrece enviar la etiqueta a la app NIIMBOT; si el navegador no sabe
  // compartir ficheros, se descarga para abrirla desde la app a mano
  const compartirEtiqueta = async () => {
    setPreparandoEtiqueta(true);
    try {
      const png = await etiquetaComoPng(await prepararEtiqueta());
      if (!png) throw new Error("No se pudo generar la imagen");
      const fichero = new File([png], `QR-${nif}.png`, { type: "image/png" });

      if (navigator.canShare?.({ files: [fichero] })) {
        await navigator.share({ files: [fichero], title: t('button.labelTitle') });
        return;
      }

      const url = URL.createObjectURL(png);
      const enlace = document.createElement("a");
      enlace.href = url;
      enlace.download = fichero.name;
      enlace.click();
      URL.revokeObjectURL(url);
      toast.info(t('button.labelDownloaded'));
    } catch (e) {
      // Cancelar el menu de compartir no es un error que haya que avisar
      if (e?.name !== "AbortError") toast.error(t('button.labelError'));
    } finally {
      setPreparandoEtiqueta(false);
    }
  };

  const imprimirEtiqueta = async () => {
    if (!hayBluetooth()) {
      await compartirEtiqueta();
      return;
    }
    setPreparandoEtiqueta(true);
    try {
      await imprimirPorBluetooth();
    } catch (e) {
      // Elegir ningun dispositivo en el selector del navegador no es un fallo
      if (e?.name !== "NotFoundError") {
        console.log(e);
        toast.error(t('button.btError'));
      }
    } finally {
      setPreparandoEtiqueta(false);
    }
  };

  const escaparHtml = (texto) =>
    String(texto ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

  // Devuelve el SVG del modal escalado al tamaño pedido, listo para incrustar
  const qrComoSvg = (lado) => {
    const svg = areaQr.current?.querySelector("svg");
    if (!svg) return "";
    const copia = svg.cloneNode(true);
    copia.setAttribute("width", lado);
    copia.setAttribute("height", lado);
    return new XMLSerializer().serializeToString(copia);
  };

  const imprimir = () => {
    const lineas = [
      datosComunidad?.direccion,
      lineaCodigoPostal(datosComunidad?.codigoPostal, datosComunidad?.municipio),
    ].filter(Boolean).map(escaparHtml).join("<br />");
    const ventanaImprimir = window.open("", "_blank", "width=600,height=800");
    ventanaImprimir.document.write(`
      <html>
        <head>
        <title>${t('button.printWindowTitle')}</title>
        <style>
          /* Sin alto fijo: con height:100vh el navegador sacaba una pagina de mas */
          @page { margin: 12mm; }
          html, body { margin: 0; padding: 0; }
          .etiqueta { text-align: center; font-family: Arial, Helvetica, sans-serif; }
          .etiqueta svg { display: block; margin: 0 auto; }
          .direccion { margin: 20px 0 0; font-size: 34px; font-weight: bold; line-height: 1.3; }
        </style>
        </head>
        <body>
        <div class="etiqueta">
          ${qrComoSvg(400)}
          ${lineas ? `<p class="direccion">${lineas}</p>` : ""}
        </div>
        <script>
          // El codigo va incrustado, basta con esperar al render de la ventana
          (function () {
            var lanzado = false;
            function lanzar() {
              if (lanzado) return;
              lanzado = true;
              window.print();
              window.close();
            }
            if (document.readyState === "complete") setTimeout(lanzar, 0);
            else window.addEventListener("load", lanzar);
            setTimeout(lanzar, 2000);
          })();
        </script>
        </body>
      </html>
      `);
    ventanaImprimir.document.close();
    ventanaImprimir.focus();
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <button
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: !colocado ? COLOR_SIN_COLOCAR : omniaHogar ? COLOR_CON_OMNIA : COLOR_SOLO_CAE,
          }}
          title={!colocado ? t('button.generate') : omniaHogar ? t('button.placedOmnia') : t('button.placedCae')}
          onClick={abrir}
        >
          <AiOutlineQrcode size={22} />
        </button>
      </Box>
      <Dialog open={abierto} onClose={cerrar} maxWidth="xs" fullWidth>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 200,
            position: "relative",
            pb: 6,
          }}
        >
          {cargando ? (
            <CircularProgress />
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : token ? (
            <>
              <div id="qr-print-area" ref={areaQr} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <QRCode value={`${window.location.origin}/qr/${token}`} size={160} />
              </div>
              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={imprimirEtiqueta}
                  disabled={preparandoEtiqueta}
                >
                  {t('button.print')}
                </Button>
                <Button
                  variant={colocado ? "outlined" : "contained"}
                  color={colocado ? "error" : "success"}
                  onClick={cambiarColocado}
                  disabled={guardandoColocado}
                >
                  {colocado ? t('button.markRemoved') : t('button.markPlaced')}
                </Button>
              </Box>
              {hayBluetooth() && (
                <Typography
                  component="button"
                  type="button"
                  onClick={compartirEtiqueta}
                  disabled={preparandoEtiqueta}
                  sx={{
                    mt: 1,
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    color: "#666",
                    fontSize: '0.8rem',
                    textDecoration: "underline",
                  }}
                >
                  {t('button.saveImage')}
                </Typography>
              )}
              <Typography
                component="button"
                type="button"
                onClick={imprimir}
                sx={{
                  mt: 1,
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: "#666",
                  fontSize: '0.8rem',
                  textDecoration: "underline",
                }}
              >
                {t('button.traditionalPrint')}
              </Typography>
              <Typography sx={{ pt: 1, fontSize: '0.8rem' }}>
                <i>{t('button.scanHint')}</i>
              </Typography>
              {colocado && (
                <Typography sx={{ pt: 0.5, fontSize: '0.8rem', color: omniaHogar ? COLOR_CON_OMNIA : COLOR_SOLO_CAE }}>
                  {t('button.alreadyPlaced')}
                </Typography>
              )}
              <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #eee", width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Typography sx={{ fontSize: "0.75rem", color: "#777" }}>
                    {t('button.legendTitle')}
                  </Typography>
                  {leyenda.map(({ clave, color, activo }) => (
                    <Box key={clave} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <AiOutlineQrcode size={16} color={color} />
                      <Typography sx={{ fontSize: "0.75rem", color: activo ? "#333" : "#999", fontWeight: activo ? 700 : 400 }}>
                        {t(clave)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          ) : null}
          <Button
            onClick={cerrar}
            sx={{
              position: "absolute",
              bottom: 16,
              right: 16,
              minWidth: 90,
            }}
          >
            {t('button.close')}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BotonGenerarQR;
